import React, { useEffect, useRef, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import MediaService from "../../services/KangarooServices/MediaServices";
import MediaGroupService from "../../services/KangarooServices/MediaGroupService";
import { getIn } from "formik";

const RichTextEditor = ({ formik, fieldName }) => {
  const editorRef = useRef();
  const [showMediaPopup, setShowMediaPopup] = useState(false);
  const [pendingLoader, setPendingLoader] = useState(null);
  const [mediaGroup, setMediaGroup] = useState([]);
  const baseUrl = window.APP_CONFIG.api2.baseapi; // Make sure this is correct
  console.log("🚀 ~ RichTextEditor ~ baseUrl:", baseUrl);

  // Fetch media groups
  const fetchGroup = async () => {
    try {
      const response = await MediaGroupService.getList({
        pageNumber: 1,
        pageSize: 1000,
      });
      setMediaGroup(response?.items || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchGroup();
  }, []);

  // --- Custom Upload Adapter ---
  class MyUploadAdapter {
    constructor(loader) {
      this.loader = loader;
      this.resolve = null;
      this.reject = null;
    }

    upload() {
      // Pause upload until user selects media group
      setPendingLoader(this);
      setShowMediaPopup(true);

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    }

    async _doUpload(mediaGroupId) {
      const file = await this.loader.file;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("mediaGroupId", mediaGroupId);

      const res = await MediaService.createSingle(formData);

      const imageUrl = `${baseUrl}${res.url}`;
      return { default: imageUrl };
    }

    abort() {
      if (this.reject) this.reject();
    }
  }

  function MyCustomUploadAdapterPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) =>
      new MyUploadAdapter(loader);
  }

  // --- Handle media group selection ---
  const handleMediaGroupSelect = async (mediaGroupId) => {
    if (!pendingLoader) return;

    setShowMediaPopup(false);
    try {
      const data = await pendingLoader._doUpload(mediaGroupId);

      // This resolves CKEditor upload and inserts the image
      pendingLoader.resolve(data);
    } catch (err) {
      pendingLoader.reject(err);
    } finally {
      setPendingLoader(null);
    }
  };

  return (
    <div style={{ minHeight: 300, position: "relative" }}>
      <CKEditor
        key={fieldName}
        editor={ClassicEditor}
        data={getIn(formik.values, fieldName) || ""}
        config={{
          extraPlugins: [MyCustomUploadAdapterPlugin],
          toolbar: [
            "heading",
            "|",
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "|",
            "link",
            "bulletedList",
            "numberedList",
            "|",
            "insertTable",
            "blockQuote",
            "imageUpload",
            "horizontalLine", // <-- add this
            "|",
            "undo",
            "redo",
          ],

          image: {
            toolbar: [
              "imageStyle:inline",
              "imageStyle:alignLeft",
              "imageStyle:alignCenter",
              "imageStyle:alignRight",
              "|",
              "imageTextAlternative",
            ],
            styles: ["inline", "alignLeft", "alignCenter", "alignRight"],
          },
        }}
        onReady={(editor) => {
          editorRef.current = editor;

          const currentValue = getIn(formik.values, fieldName) || "";
          if (currentValue) {
            editor.setData(currentValue);
          }
        }}
        onChange={(event, editor) => {
          const data = editor.getData();
          if (data !== getIn(formik.values, fieldName)) {
            formik.setFieldValue(fieldName, data);
          }
        }}
      />

      {/* Media Group Popup */}
      {showMediaPopup && (
        <div
          style={{
            position: "absolute",
            top: 50,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#fff",
            padding: 20,
            border: "1px solid #ccc",
            zIndex: 1000,
          }}
        >
          <h4>Select Media Group</h4>
          {mediaGroup.map((group) => (
            <button
              key={group.id}
              style={{ margin: 5 }}
              onClick={() => handleMediaGroupSelect(group.id)}
            >
              {group.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
